import React, { useState, useRef, useEffect } from "react";
import { Box, Button, TextField } from "@mui/material";

const SESSION_ID = `session_${Date.now()}`;
const SILENCE_THRESHOLD = 0.05; // Threshold for detecting silence
const MIN_AUDIO_DURATION = 2000; // Minimum 2 seconds of audio before sending
const SILENCE_DETECTION_INTERVAL = 500; // Check for silence every 500ms

const AppLayout = () => {
  const [messages, setMessages] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [inputText, setInputText] = useState("");
  const audioContextRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const audioChunks = useRef([]); // Collect audio chunks

  useEffect(() => {
    // Start a new session when the component mounts
    const startSession = async () => {
      try {
        await fetch("http://localhost:3002/api/start-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId: SESSION_ID }),
        });
        console.log("Session started:", SESSION_ID);
      } catch (err) {
        console.error("Failed to start session:", err.message);
      }
    };

    startSession();
  }, []);

  /**
   * Ensure AudioContext is Resumed
   */
  const ensureAudioContext = async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }

    if (audioContextRef.current.state === "suspended") {
      await audioContextRef.current.resume();
      console.log("AudioContext resumed.");
    }
  };

  /**
   * Handle Sending Text to ElevenLabs
   */
  const handleSendText = async () => {
    if (!inputText.trim()) return;

    try {
      // Add user message
      setMessages((prev) => [...prev, { role: "User", content: inputText }]);
      const textToSend = inputText;
      setInputText(""); // Clear input field

      // Send user input to the server
      const response = await fetch("http://localhost:3002/api/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: SESSION_ID,
          userInput: textToSend,
        }),
      });

      if (!response.ok) throw new Error("Failed to communicate with ElevenLabs backend.");

      const data = await response.json();

      // Add AI response
      setMessages((prev) => [...prev, { role: "AI", content: data.context }]);

      // Play the audio response
      const audio = new Audio(data.audio);
      audio.play();
    } catch (err) {
      console.error("Error sending text:", err.message);
    }
  };

  /**
   * Handle Audio Chunk Submission
   */
  const handleAudioChunk = async (blob) => {
    const formData = new FormData();
    formData.append("audio", blob, "chunk.webm");
    formData.append("sessionId", SESSION_ID);

    try {
      console.log("Sending audio chunk...");
      const response = await fetch("http://localhost:3002/api/tts", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Failed to send audio.");

      const data = await response.json();
      setMessages((prev) => [...prev, { role: "AI", content: data.context }]);

      const audio = new Audio(data.audio);
      audio.play();
    } catch (err) {
      console.error("Error sending audio:", err.message);
    }
  };

  /**
   * Start Recording with Silence Detection
   */
  const handleStartRecording = async () => {
    if (isRecording) return;

    await ensureAudioContext();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext = audioContextRef.current;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      source.connect(analyser);

      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder;
      audioStreamRef.current = stream;

      const pcmData = new Float32Array(analyser.fftSize);
      let isSilenceDetected = false;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunks.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (audioChunks.current.length > 0) {
          const blob = new Blob(audioChunks.current, { type: "audio/webm" });
          audioChunks.current = []; // Clear chunks
          await handleAudioChunk(blob);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);

      silenceTimerRef.current = setInterval(() => {
        analyser.getFloatTimeDomainData(pcmData);
        const rms = Math.sqrt(
          pcmData.reduce((sum, sample) => sum + sample ** 2, 0) / pcmData.length
        );

        console.log("RMS value:", rms);

        if (rms < SILENCE_THRESHOLD) {
          if (!isSilenceDetected) {
            console.log("Silence detected. Stopping recording...");
            isSilenceDetected = true;
            mediaRecorder.stop();
            mediaRecorder.start(); // Restart recording after silence
          }
        } else {
          isSilenceDetected = false;
        }
      }, SILENCE_DETECTION_INTERVAL);
    } catch (err) {
      console.error("Error starting recording:", err.message);
    }
  };

  /**
   * Stop Recording and Cleanup
   */
  const handleStopRecording = () => {
    if (!isRecording) return;

    if (mediaRecorderRef.current) mediaRecorderRef.current.stop();
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    if (silenceTimerRef.current) {
      clearInterval(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    setIsRecording(false);
  };

  return (
    <Box sx={{ padding: 2 }}>
      <div>
        {messages.map((msg, idx) => (
          <div key={idx} style={{ marginBottom: "10px" }}>
            <b>{msg.role}:</b> {msg.content}
          </div>
        ))}
      </div>

      <Box sx={{ marginTop: 2 }}>
        <TextField
          label="Your Message"
          variant="outlined"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          sx={{ marginRight: 2 }}
        />

        <Button variant="contained" color="primary" onClick={handleSendText}>
          Send Text
        </Button>

        <Button
          variant="contained"
          color="secondary"
          sx={{ marginLeft: 2 }}
          onClick={handleStartRecording}
        >
          Start Recording
        </Button>

        <Button
          variant="contained"
          color="error"
          sx={{ marginLeft: 2 }}
          onClick={handleStopRecording}
        >
          Stop Recording
        </Button>
      </Box>
    </Box>
  );
};

export default AppLayout;